import React, { createContext, useContext, useId, useMemo } from 'react';

import { Input, InputAffix, InputIcon, InputRoot } from '@/design-system/input';
import { Label } from '@/design-system/label';
import { cn } from '@/design-system/lib/utils';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/design-system/select';
import { Textarea } from '@/design-system/textarea';

/**
 * Thin compositions over the kit input primitives for the WMS forms. Fixed
 * enums use the kit Select (Radix); entity lists are server-searched kit
 * SearchSelects (`pickers.tsx`, `StorageSelect.tsx`). No OS-native select element.
 */

/**
 * What a FormField tells the control inside it: the label's id (pickers that
 * can't take a `<label htmlFor>` name themselves with it) and the id of the
 * error / hint line, so the control points at it via `aria-describedby`.
 */
export interface FieldA11y {
  labelId: string;
  describedBy: string | undefined;
  invalid: boolean;
}

const FieldContext = createContext<FieldA11y | null>(null);

/** The enclosing FormField's a11y ids (null outside a FormField). */
export const useFieldA11y = (): FieldA11y | null => useContext(FieldContext);

interface FormFieldProps {
  label: string;
  htmlFor?: string;
  required?: boolean;
  optional?: boolean;
  hint?: React.ReactNode;
  error?: string | null;
  className?: string;
  /** `data-testid` on the field wrapper (E2E scope for a picker inside). */
  testId?: string;
  children: React.ReactNode;
}

export const FormField: React.FC<FormFieldProps> = ({
  label,
  htmlFor,
  required,
  optional,
  hint,
  error,
  className,
  testId,
  children,
}) => {
  const uid = useId();
  const labelId = `${uid}-label`;
  const messageId = `${uid}-message`;
  const hasMessage = !!error || !!hint;
  const a11y = useMemo<FieldA11y>(
    () => ({
      labelId,
      describedBy: hasMessage ? messageId : undefined,
      invalid: !!error,
    }),
    [labelId, messageId, hasMessage, error]
  );
  return (
    <div
      className={cn('flex min-w-0 flex-col gap-1.5', className)}
      data-testid={testId}
    >
      <Label
        id={labelId}
        htmlFor={htmlFor}
        required={required}
        optional={optional}
      >
        {label}
      </Label>
      <FieldContext.Provider value={a11y}>{children}</FieldContext.Provider>
      {error ? (
        <p
          id={messageId}
          className="text-[11px] leading-relaxed text-(--state-error-base)"
          role="alert"
        >
          {error}
        </p>
      ) : hint ? (
        <p
          id={messageId}
          className="text-[11px] leading-relaxed text-text-sub-600"
        >
          {hint}
        </p>
      ) : null}
    </div>
  );
};

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

interface EnumSelectProps {
  id?: string;
  value: string;
  onChange: (value: string) => void;
  options: SelectOption[];
  placeholder?: string;
  disabled?: boolean;
  invalid?: boolean;
  size?: 'sm' | 'md';
  className?: string;
  'aria-label'?: string;
  'aria-describedby'?: string;
  'data-testid'?: string;
}

/**
 * Kit Select over a small fixed list (status, UoM, service, category …):
 * the options are known up front, nothing is searched or paged. A value that
 * is not among the options shows the placeholder.
 */
export const EnumSelect: React.FC<EnumSelectProps> = ({
  id,
  value,
  onChange,
  options,
  placeholder,
  disabled,
  invalid,
  size = 'md',
  className,
  'aria-label': ariaLabel,
  'aria-describedby': describedBy,
  'data-testid': testId,
}) => {
  const field = useFieldA11y();
  return (
    <Select value={value} onValueChange={onChange} disabled={disabled}>
      <SelectTrigger
        id={id}
        size={size}
        aria-label={ariaLabel}
        aria-describedby={describedBy ?? field?.describedBy}
        aria-invalid={invalid || undefined}
        data-testid={testId}
        className={className}
      >
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        {options.map((o) => (
          <SelectItem key={o.value} value={o.value} disabled={o.disabled}>
            {o.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};

interface TextInputProps extends Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'size' | 'onChange'
> {
  value: string;
  onChange: (value: string) => void;
  size?: 'sm' | 'md';
  invalid?: boolean;
  icon?: string;
  suffix?: React.ReactNode;
  rootClassName?: string;
  /** The `<input>` element (e.g. to move focus to it). */
  ref?: React.Ref<HTMLInputElement>;
}

export const TextInput: React.FC<TextInputProps> = ({
  value,
  onChange,
  size = 'md',
  invalid,
  icon,
  suffix,
  rootClassName,
  className,
  ...rest
}) => {
  const field = useFieldA11y();
  return (
    <InputRoot size={size} invalid={invalid} className={rootClassName}>
      {icon && (
        <InputIcon>
          <i className={cn(icon, 'text-[16px]')} aria-hidden />
        </InputIcon>
      )}
      <Input
        {...rest}
        aria-describedby={rest['aria-describedby'] ?? field?.describedBy}
        aria-invalid={invalid || undefined}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={cn('min-w-0', className)}
      />
      {suffix && <InputAffix>{suffix}</InputAffix>}
    </InputRoot>
  );
};

interface TextAreaProps extends Omit<
  React.TextareaHTMLAttributes<HTMLTextAreaElement>,
  'onChange'
> {
  value: string;
  onChange: (value: string) => void;
  invalid?: boolean;
}

export const TextArea: React.FC<TextAreaProps> = ({
  value,
  onChange,
  invalid,
  className,
  rows = 2,
  ...rest
}) => {
  const field = useFieldA11y();
  return (
    <Textarea
      {...rest}
      aria-describedby={rest['aria-describedby'] ?? field?.describedBy}
      rows={rows}
      invalid={invalid}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className={cn('min-h-0', className)}
    />
  );
};
